import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { IconButton } from "@mui/material";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import { IIf, IF_OPERATOR_LABELS, IfOperator } from "../../../models/interactions/If";
import useUpdateSequenceInteraction from "../node_previews/UpdateSequenceInteraction.hook";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import { VariableType } from "../../../models/interactions/Variable";

const NUMERIC_ONLY_OPERATORS = new Set([
  IfOperator.Less,
  IfOperator.LessOrEqual,
  IfOperator.Greater,
  IfOperator.GreaterOrEqual,
]);

const ANY_TYPE_OPERATORS = new Set([
  IfOperator.Equal,
  IfOperator.NotEqual,
]);

export default function IfOperatorDropdown({
  nodeId,
  interaction,
}: {
  nodeId: string;
  interaction: IIf;
}) {
  const { updateInteraction } = useUpdateSequenceInteraction();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const variables = useSelector(selectSequenceVariables);

  return (
    <div className="noDrag">
      <DropdownMenu.Root
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        modal={false}
      >
        <DropdownMenu.Trigger asChild>
          <IconButton size="small" className="!p-0 !min-w-0">
            <ChangeCircleOutlinedIcon
              className="text-creator-primary"
              sx={{ fontSize: 20 }}
            />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className="flex flex-col max-h-[200px] pit-creator-scrollbar overflow-y-auto bg-creator-surface overflow-x-hidden rounded-[10px] shadow-lg z-40"
            style={{
              padding: "6px",
              minWidth: "160px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            }}
            onWheel={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sideOffset={5}
          >
            {Object.entries(IF_OPERATOR_LABELS).map(([op, label]) => (
              <DropdownMenu.Item
                key={op}
                className="rounded-md transition-colors cursor-pointer focus:outline-none"
                onSelect={() => {
                  const newOperator = op as IfOperator;
                  const selectedVariable = variables.find(
                    (v) => v.id === interaction.variable
                  );
                  const needsClear =
                    NUMERIC_ONLY_OPERATORS.has(newOperator) &&
                    selectedVariable &&
                    selectedVariable.type !== VariableType.Number;

                  updateInteraction(nodeId, {
                    ...interaction,
                    operator: newOperator,
                    ...(needsClear && { variable: "", match: "" }),
                  } as IIf);
                  setDropdownOpen(false);
                }}
              >
                <div
                  className={classNames(
                    "flex items-center px-3 py-2 hover:bg-creator-primary-background rounded-md",
                    interaction.operator === op && "bg-creator-primary-background"
                  )}
                >
                  <span className="text-[14px] font-medium text-creator-text-sub font-poppins">
                    {label}
                  </span>
                </div>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
